import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import Link from 'next/link';
import { Fragment, useMemo, memo, useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/solid';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { useTheme } from 'next-themes';

import ProfileCircle from 'components/generic-components/ProfileCircle';
import SearchBar from 'components/container-components/navbar/navSearchBar/SearchBar';
import CollapsedSearchButton from 'components/container-components/navbar/navSearchBar/CollapsedSearchButton';
import { useUser } from 'context/user';
import { useNavigation } from './NavigationContext';
import ArrowDown from 'icons/ArrowDown';
import { AdminRoutes, AuthRoutes, ContractorRoutes } from 'routes';
import { getInitials } from 'utils/string';
import Settings from 'icons/Settings';
import Feedback from 'icons/Feedback';
import CopyableText from 'components/generic-components/copyableText';
import { useModal } from 'hooks/useModal';
import { showIDToCopy } from 'utils/user';
import { getAccountSwitchingOptions } from 'utils/affiliations';
import { isWhiteLabelEnabled } from 'utils/whiteLabelConfig';
import { IRoleType } from 'context/user/user.type';
import NotificationDropdown from './NotificationDropdown';

const SwitchAccountModal = dynamic(() => import('./SwitchAccountModal'), {
  ssr: false,
});
const SendFeedbackModal = dynamic(() => import('components/app-components/modals/SendFeedbackModal'), { ssr: false });
const CommandPalette = dynamic(() => import('./CommandPalette'), {
  ssr: false,
});
const SettingsIcon = memo(() => <Settings className="h-5 w-5 fill-neutral-600 group-hover:fill-neutral-900" />);
const FeedbackIcon = memo(() => <Feedback className="h-4 w-4 text-neutral-800" />);

const MainNavRightSide = ({
  isFallback,
  handleFallbackRouting,
  searchCollapsed = false,
}: {
  isFallback?: boolean;
  handleFallbackRouting?: (href: string) => void;
  userRoleType?: IRoleType;
  searchCollapsed?: boolean;
}) => {
  const { user, currentCompany, affiliationsByCompany, isLoading, roleType, logout } = useUser() || {};
  const { isMobileMenuOpen, toggleMobileMenu, hasSidebar } = useNavigation();
  const { resolvedTheme, setTheme } = useTheme();
  // next-themes only knows the real theme after mount; render the default
  // (light) icon until then so server and client markup match.
  const [themeReady, setThemeReady] = useState(false);
  useEffect(() => setThemeReady(true), []);
  const isDarkMode = themeReady && resolvedTheme === 'dark';
  const toggleTheme = useCallback(() => setTheme(isDarkMode ? 'light' : 'dark'), [isDarkMode, setTheme]);
  const isWorker = roleType === 'worker';
  const isManager = roleType === 'manager';
  const { firstName = '', lastName = '', userID = '' } = user || {};
  const showUserIDToCopy = useMemo(() => showIDToCopy(), []);
  const switchAccountModal = useModal();
  const feedbackModal = useModal();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopImmediatePropagation();
        setIsCommandPaletteOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', handler, { capture: true });
    return () => document.removeEventListener('keydown', handler, { capture: true });
  }, []);

  const accountSettingsRoute = isWorker ? ContractorRoutes.AccountPreferences : AdminRoutes.AccountPreferences;
  const userInitials = useMemo(() => getInitials(firstName, lastName), [firstName, lastName]);

  const handleLogout = useCallback(async () => {
    !isFallback ? await logout() : handleFallbackRouting(AuthRoutes.Logout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFallback, handleFallbackRouting]);

  const goToHelpCenter = useCallback(() => {
    window.open('https://intercom.help/thera/en/', '_blank');
  }, []);

  const handleSwitchAccount = () => {
    switchAccountModal.handleOpen();
  };

  const canSwitchAccounts = useMemo(() => {
    if (!affiliationsByCompany) return false;
    const numberOfCompanies = Object.keys(affiliationsByCompany).length;
    // We require Switch Accounts on Admin side all the time so that they can add new company
    if (numberOfCompanies || isManager) {
      return true;
    } else {
      const companyId = Object.keys(affiliationsByCompany)[0];
      const affiliation = affiliationsByCompany[companyId];
      const accountSwitchOptions = getAccountSwitchingOptions(affiliation);
      return accountSwitchOptions.length > 1;
    }
  }, [affiliationsByCompany, isManager]);

  return (
    <div className="flex items-center">
      <div className="hidden md:block">
        {!isFallback &&
          user &&
          (searchCollapsed ? (
            <CollapsedSearchButton onClick={() => setIsCommandPaletteOpen(true)} />
          ) : (
            <SearchBar onOpenCommandPalette={() => setIsCommandPaletteOpen(true)} />
          ))}
      </div>
      {isWorker && currentCompany && (
        <div className="ml-3 block max-w-[200px] truncate text-ellipsis rounded-full border border-therablue px-2 py-1 text-center text-sm font-medium text-neutral-500 lg:visible">
          {currentCompany?.dbaName}
        </div>
      )}

      <ul className="ml-3 flex items-center space-x-3">
        {isManager && (
          <li className="inline-flex">
            <NotificationDropdown />
          </li>
        )}
        <li className="inline-flex items-center">
          <button
            onClick={feedbackModal.handleOpen}
            className="flex h-9 items-center gap-2 rounded-[25px] bg-neutral-100 px-4 py-2 text-neutral-800 hover:bg-neutral-200"
            data-testid="feedback-button"
            aria-label="Send feedback"
          >
            <FeedbackIcon />
            <span className="text-sm font-medium">Feedback</span>
          </button>
        </li>
        <li className="inline-flex">
          <Link
            onClick={() => handleFallbackRouting(accountSettingsRoute)}
            href={accountSettingsRoute}
            className="group flex h-8 w-8 items-center justify-center transition-all hover:rounded-full hover:bg-neutral-100"
            data-testid="account-settings-link"
          >
            <SettingsIcon />
          </Link>
        </li>
        <li className="z-[100] inline-flex">
          <Menu>
            <div className="relative flex justify-center">
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems
                  modal={false}
                  className="absolute right-0 mt-10 min-w-[200px] rounded-2xl bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:ring-white/10 lg:rounded-md"
                >
                  {' '}
                  {!isFallback && (
                    <div className="px-4 py-2">
                      <p className="text-xs font-medium text-neutral-500">{currentCompany?.companyName}</p>
                      <p className="mt-0.5 text-sm font-medium" data-sensitive-copy-admins>
                        {user?.firstName} {user?.lastName}
                      </p>
                      {/* <p className="text-xs font-medium text-neutral-800">{userEmploymentType}</p> */}
                      <p className="mt-0.5 text-xs font-medium text-neutral-500" data-sensitive-copy-admins>
                        {user?.email}
                      </p>
                      {showUserIDToCopy && (
                        <CopyableText
                          text={userID}
                          containerClassName="mt-2"
                          textClassName="text-xs text-[#295E73] dark:text-neutral-400"
                          showCopyIcon={false}
                          trim
                        />
                      )}
                    </div>
                  )}
                  {canSwitchAccounts && (
                    <MenuItem
                      as="a"
                      onClick={handleSwitchAccount}
                      disabled={isLoading || !affiliationsByCompany}
                      className="flex border-t border-neutral-100 px-4 py-2 text-sm text-neutral-800 hover:cursor-pointer hover:bg-gray-100"
                    >
                      Switch Accounts
                    </MenuItem>
                  )}
                  <MenuItem
                    as="a"
                    onClick={goToHelpCenter}
                    className="flex border-t border-neutral-100 px-4 py-2 text-sm text-neutral-800 hover:cursor-pointer hover:bg-gray-100"
                  >
                    Help Center
                  </MenuItem>
                  {/* White-label tenants are forced to light in _app.tsx until they have dark logo assets */}
                  {!isWhiteLabelEnabled() && (
                    <MenuItem
                      as="a"
                      onClick={toggleTheme}
                      className="flex items-center border-t border-neutral-100 px-4 py-2 text-sm text-neutral-800 hover:cursor-pointer hover:bg-gray-100"
                      data-testid="theme-toggle"
                    >
                      {isDarkMode ? (
                        <SunIcon className="-ml-0.5 mr-2 h-4 w-5" />
                      ) : (
                        <MoonIcon className="-ml-0.5 mr-2 h-4 w-5" />
                      )}
                      {isDarkMode ? 'Light mode' : 'Dark mode'}
                    </MenuItem>
                  )}
                  <MenuItem
                    as="a"
                    onClick={handleLogout}
                    className="flex border-t border-neutral-100 px-4 py-2 text-sm text-orange-700 hover:cursor-pointer hover:bg-gray-100"
                  >
                    <ArrowRightStartOnRectangleIcon className="-ml-0.5 mr-2 h-5 w-5" />
                    Logout
                  </MenuItem>
                </MenuItems>
              </Transition>
              <MenuButton as={Fragment}>
                <a className="flex cursor-pointer items-center" data-testid="user-profile-link">
                  <ProfileCircle
                    text={isFallback ? '' : userInitials}
                    id={(!isFallback && userID) || ''}
                    size="sm"
                    textClassName="text-sm"
                  />
                  <ArrowDown className="ml-1 hidden md:block" />
                </a>
              </MenuButton>

              {/* Hamburger Menu - Mobile Only */}
              {hasSidebar && (
                <button
                  onClick={toggleMobileMenu}
                  className={`relative ml-3 flex h-8 w-8 flex-col items-center justify-center rounded-full transition-all hover:bg-neutral-100 lg:hidden ${
                    isMobileMenuOpen ? '!fixed right-0 top-0 z-[100]' : ''
                  }`}
                  aria-label="Toggle mobile menu"
                >
                  <span
                    className={`absolute h-0.5 w-5 rounded-full bg-neutral-600 transition-all dark:bg-neutral-400 ${
                      isMobileMenuOpen ? 'translate-y-0 rotate-45' : '-translate-y-1.5'
                    }`}
                  ></span>
                  <span
                    className={`h-0.5 w-5 rounded-full bg-neutral-600 transition-all dark:bg-neutral-400 ${
                      isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                    }`}
                  ></span>
                  <span
                    className={`absolute h-0.5 w-5 rounded-full bg-neutral-600 transition-all dark:bg-neutral-400 ${
                      isMobileMenuOpen ? 'translate-y-0 -rotate-45' : 'translate-y-1.5'
                    }`}
                  ></span>
                </button>
              )}
            </div>
          </Menu>
        </li>
      </ul>

      {affiliationsByCompany && (
        <SwitchAccountModal isOpen={switchAccountModal.isOpen} onClose={switchAccountModal.handleClose} />
      )}
      <SendFeedbackModal isOpen={feedbackModal.isOpen} onClose={feedbackModal.handleClose} />
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={() => setIsCommandPaletteOpen(false)} />
    </div>
  );
};

export default memo(MainNavRightSide);
